import { useContext } from 'react';
import { format, isValid } from 'date-fns';

import { Box, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconLockClosedOutline } from '@zillow/constellation-icons';

import { RMW_PATH } from '../../../../pathsManifest';
import { FreemiumContext } from '../../../Freemium/context';
import { RMWContext } from '../../../../providers/RMWContext';
import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';

import { RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

type Props = {
  activeProductExpirationTimestamp?: number;
  isActiveProductTypeFree?: boolean;
  isFromListingModule?: boolean;
};

const PremiumCTA = ({ activeProductExpirationTimestamp, isActiveProductTypeFree, isFromListingModule }: Props) => {
  const {
    loadingByComponent: { ConnectedListing: isConnectedListingLoading, ListingModule: isListingModuleLoading },
  } = useContext(LoadingMapContext);

  const { freemiumState } = useContext(FreemiumContext);
  const { paths } = useContext(RMWContext);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const formattedActiveProductExpirationDate =
    isValid(new Date(Number(activeProductExpirationTimestamp))) &&
    format(new Date(Number(activeProductExpirationTimestamp)), 'MMM\xa0d');

  const isPageLoading = isFromListingModule ? isListingModuleLoading : isConnectedListingLoading;

  const handleClick = () => {
    window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/${RMW_PATH.PREMIUM_ANALYTICS}`);
  };

  const handleUnlockClick = () => {
    const element = freemiumState.featuresOfPremiumRef?.current;
    if (element) {
      const offsetPosition = element.offsetTop - 100;

      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <Box
      css={{
        marginTop: { base: '400', lg: '600' },
        padding: '400',
        cursor: isActiveProductTypeFree ? 'pointer' : 'default',
        backgroundColor: isActiveProductTypeFree ? 'bg.softest' : 'legacy.coolGray.200',
      }}
      onClick={isActiveProductTypeFree ? handleUnlockClick : undefined}
    >
      {!isPageLoading && (
        <>
          {isActiveProductTypeFree ? (
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Icon size="md" css={{ color: 'icon.subtle' }} render={<IconLockClosedOutline />} />
              <Text textStyle="body-sm" color="text.subtle" css={{ marginLeft: '200' }}>
                Unlock your custom data
              </Text>
            </Box>
          ) : (
            <Box css={{ display: 'flex', flexDirection: { base: 'column', xl: 'row' }, alignItems: 'center' }}>
              <TextButton textStyle="body-sm-bold" onClick={handleClick}>
                Explore premium analytics
              </TextButton>

              <Box css={{ marginRight: '400' }} />

              <Text textStyle="body-sm">
                Premium benefits expire&nbsp;
                <Text textStyle="body-sm-bold">{formattedActiveProductExpirationDate}</Text>
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PremiumCTA;
